package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import dev.lingvo.site.components.CommandPaletteState
import dev.lingvo.shared.TranslationState
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import kotlinx.browser.document
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.EventListener
import org.w3c.dom.events.KeyboardEvent

internal enum class EditorTextEntryContext {
    NONE,
    TARGET_TEXT,
    UTILITY_TEXT,
    OTHER_TEXT,
}

internal data class EditorShortcutInput(
    val key: String,
    /**
     * `event.code` — the physical key, independent of layout and of modifier composition.
     *
     * Only the Alt branch reads this. macOS Option is a compose modifier, so Option+P arrives as
     * `key = "π"` and no amount of case-insensitive comparison against "p" will match it. `code`
     * is `KeyP` whatever the Option key did to the character.
     *
     * Defaulted so existing call sites and tests that do not care still construct.
     */
    val code: String = "",
    val command: Boolean = false,
    val shift: Boolean = false,
    val alt: Boolean = false,
    val context: EditorTextEntryContext = EditorTextEntryContext.NONE,
    val modalOpen: Boolean = false,
    val defaultPrevented: Boolean = false,
    val composing: Boolean = false,
    val ownedCommandHandled: Boolean = false,
)

internal sealed interface EditorShortcutAction {
    data object ApproveAndNext : EditorShortcutAction
    data object SaveAndNext : EditorShortcutAction
    data object Save : EditorShortcutAction
    data object CopySource : EditorShortcutAction
    data object InsertNextPlaceholder : EditorShortcutAction
    data object InsertOpeningQuote : EditorShortcutAction
    data object InsertClosingQuote : EditorShortcutAction
    data class ActivateAssistResult(val index: Int) : EditorShortcutAction
    data object CloseTopSurface : EditorShortcutAction
    data object PreviousKey : EditorShortcutAction
    data object NextKey : EditorShortcutAction
    data object FocusTarget : EditorShortcutAction
    data object FocusSearch : EditorShortcutAction

    /**
     * Open history without leaving the field.
     *
     * An Alt chord because it has to be one: the Alt branch below returns before the guard that
     * silences shortcuts while the caret is in the target field, and the caret is in the target
     * field the entire time somebody is working. A non-Alt binding for history would be a binding
     * that never fires when history is wanted.
     */
    data object OpenHistory : EditorShortcutAction
    data object OpenShortcutHelp : EditorShortcutAction

    /**
     * Translate the current key with the remembered — or, failing that, the first enabled —
     * provider. Machine translation is the amended persona's highest-frequency action and had no
     * keyboard path at all: it lived behind an unlabelled sparkle icon and re-offered up to six
     * providers on every key because nothing was remembered.
     */
    data object MachineTranslate : EditorShortcutAction

    /**
     * Jump to the next string in the loaded queue that still has no translation.
     *
     * The editor's queue is ordered by key, not by what is left, so working through 38 untranslated
     * strings among 412 meant scrolling and reading state chips. This is the same walk the "Next"
     * in "Save and next" does, with a question asked at each step.
     */
    data object NextUntranslated : EditorShortcutAction

    /** The same walk, asking instead which string the QA engine has an objection to. */
    data object NextQaIssue : EditorShortcutAction
}

/**
 * Capability is checked after resolving the real keyboard event but before the event is consumed.
 * A denied mutation chord must remain an ordinary browser event: no preventDefault, callback,
 * optimistic state, or transport side effect.
 */
internal fun EditorShortcutAction.isAvailableTo(
    capability: ResolvedEditorCapability,
): Boolean = when (this) {
    EditorShortcutAction.ApproveAndNext -> capability.canApproveTranslations
    EditorShortcutAction.SaveAndNext,
    EditorShortcutAction.Save,
    EditorShortcutAction.InsertNextPlaceholder,
    EditorShortcutAction.InsertOpeningQuote,
    EditorShortcutAction.InsertClosingQuote,
    is EditorShortcutAction.ActivateAssistResult,
    -> capability.canWriteTranslations

    // Machine translation reserves the workspace's credits, so `POST /mt/translate` asks for
    // role >= ADMIN and the `mt:spend` scope. Alt+T is the same affordance as the source row's
    // sparkle and the Assist generate button, and it is gated with them: a chord that answers 403
    // is a control that lies about what it can do, and it is listed in the shortcut sheet too.
    EditorShortcutAction.MachineTranslate,
    -> capability.canSpendCredits

    EditorShortcutAction.CopySource,
    EditorShortcutAction.CloseTopSurface,
    EditorShortcutAction.PreviousKey,
    EditorShortcutAction.NextKey,
    EditorShortcutAction.FocusTarget,
    EditorShortcutAction.FocusSearch,
    EditorShortcutAction.OpenHistory,
    EditorShortcutAction.OpenShortcutHelp,
    // Moving the selection is reading, not writing: a viewer walking a locale's QA errors to
    // report them is exactly the person these two exist for.
    EditorShortcutAction.NextUntranslated,
    EditorShortcutAction.NextQaIssue,
    -> true
}

internal fun resolveEditorShortcut(input: EditorShortcutInput): EditorShortcutAction? {
    if (input.defaultPrevented || input.composing || input.ownedCommandHandled) return null
    if (input.key == "Escape") return EditorShortcutAction.CloseTopSurface
    if (input.modalOpen) return null
    if (input.context == EditorTextEntryContext.UTILITY_TEXT ||
        input.context == EditorTextEntryContext.OTHER_TEXT
    ) {
        return null
    }
    if (input.command && input.alt) return null

    if (input.command) {
        if (input.shift) {
            return when {
                input.key == "Enter" -> EditorShortcutAction.ApproveAndNext
                // The two queue walks. `ArrowDown` because it is the same direction the bare arrow
                // already moves, one clause narrower; `E` for the QA *error* it lands on.
                input.key == "ArrowDown" -> EditorShortcutAction.NextUntranslated
                input.key.equals("e", ignoreCase = true) -> EditorShortcutAction.NextQaIssue
                else -> null
            }
        }
        return when {
            input.key == "Enter" -> EditorShortcutAction.SaveAndNext
            input.key.equals("s", ignoreCase = true) -> EditorShortcutAction.Save
            // **Copy source is not here any more — it is `Alt+C`.** The command palette took `⌘K`,
            // which is the near-universal convention for one (VS Code, Linear, Slack, Notion,
            // GitHub), and the palette's listener mounts above the editor and calls
            // `preventDefault`, which this resolver treats as "already handled" at the top of the
            // function. Two handlers cannot share the chord; the product-specific one moved.
            else -> null
        }
    }

    if (input.alt) {
        return when (input.code) {
            // Every chord in this branch acts on the field's contents — a placeholder, a quote, a
            // suggestion, a machine draft, and now the source string. The command chords are save,
            // approve and the palette. The split means something now rather than being historical.
            "KeyC" -> EditorShortcutAction.CopySource
            "KeyP" -> EditorShortcutAction.InsertNextPlaceholder
            // The locale's own quote marks. Q and W because they sit under the left hand beside the
            // existing Alt+P, and because the pair is adjacent on the keyboard as it is in the text.
            //
            // They insert whatever `quoteStyleFor` returns for the locale being edited, not a fixed
            // pair — the same function `WRONG_QUOTE_STYLE` judges the result with, so the key that
            // types the mark and the check that grades it cannot disagree. Before this the palette
            // was reachable only with a mouse, in an editor that is otherwise driven entirely from
            // the keyboard.
            "KeyQ" -> EditorShortcutAction.InsertOpeningQuote
            "KeyW" -> EditorShortcutAction.InsertClosingQuote
            "KeyH" -> EditorShortcutAction.OpenHistory
            "KeyT" -> EditorShortcutAction.MachineTranslate
            "Digit1", "Digit2", "Digit3", "Digit4", "Digit5",
            "Digit6", "Digit7", "Digit8", "Digit9",
            -> EditorShortcutAction.ActivateAssistResult(input.code.last().digitToInt() - 1)
            // Reachable from the target field, where `?` cannot be: `?` is a character and must keep
            // typing. Without this the sheet documenting every binding was unreachable for the whole
            // session, because the caret never leaves the field.
            "Slash" -> EditorShortcutAction.OpenShortcutHelp
            else -> null
        }
    }

    if (input.context == EditorTextEntryContext.TARGET_TEXT) return null
    return when (input.key) {
        "ArrowUp" -> EditorShortcutAction.PreviousKey
        "ArrowDown" -> EditorShortcutAction.NextKey
        "Enter" -> EditorShortcutAction.FocusTarget
        "/" -> EditorShortcutAction.FocusSearch
        "?" -> EditorShortcutAction.OpenShortcutHelp
        else -> null
    }
}

@Composable
internal fun EditorKeyboardShortcuts(
    state: TranslationEditorState,
    workspace: EditorWorkspaceState,
    onCloseTopSurface: () -> Unit,
    onFocusTarget: () -> Unit,
    onFocusSearch: () -> Unit,
    onOpenHistory: () -> Unit,
    onOpenShortcutHelp: () -> Unit,
    onCopySource: () -> Unit,
    onInsertText: (String) -> Unit,
    onInsertSuggestion: (EditorSuggestion) -> Unit,
    onTab: (KeyboardEvent) -> Unit,
    onMoveSelection: (Int) -> Unit,
) {
    DisposableEffect(state, workspace) {
        val listener = EventListener { rawEvent ->
            val event = rawEvent as? KeyboardEvent ?: return@EventListener
            if (CommandPaletteState.open) return@EventListener
            if (event.key == "Tab") {
                onTab(event)
                return@EventListener
            }
            val target = event.target as? Element
            if (event.isUnmodifiedNativeActivation(target)) return@EventListener
            val action = resolveEditorShortcut(
                EditorShortcutInput(
                    key = event.key,
                    code = event.code,
                    command = event.metaKey || event.ctrlKey,
                    shift = event.shiftKey,
                    alt = event.altKey,
                    context = currentEditorTextEntryContext(),
                    modalOpen = workspace.modal != EditorModalSurface.NONE,
                    defaultPrevented = event.defaultPrevented,
                    composing = event.isComposing,
                    ownedCommandHandled = target?.closest("[data-editor-shortcut-owned='true']") != null,
                ),
            ) ?: return@EventListener
            if (!action.isAvailableTo(state.capability)) return@EventListener

            when (action) {
                EditorShortcutAction.ApproveAndNext -> {
                    event.preventDefault()
                    state.saveCurrent(approve = true, advance = true)
                }

                EditorShortcutAction.SaveAndNext -> {
                    event.preventDefault()
                    state.saveCurrent(approve = false, advance = true)
                }

                EditorShortcutAction.Save -> {
                    event.preventDefault()
                    state.saveCurrent(approve = false, advance = false)
                }

                EditorShortcutAction.NextUntranslated -> {
                    event.preventDefault()
                    if (!state.advanceTo { row -> row.target?.state.isUntranslated() }) {
                        // Said rather than silently doing nothing: a chord that moves the caret
                        // sometimes and not others, with no difference the reader can perceive,
                        // reads as a broken keyboard.
                        state.toasts.show(
                            tr(
                                "editor.editor_shortcuts.nothing_untranslated_is_loaded",
                                "Nothing untranslated is loaded. Scroll the queue for more.",
                            ),
                        )
                    }
                }

                EditorShortcutAction.NextQaIssue -> {
                    event.preventDefault()
                    val flagged = state.qaIssues.mapTo(mutableSetOf()) { it.keyId }
                    if (!state.advanceTo { row -> row.key.id in flagged }) {
                        state.toasts.show(
                            tr(
                                "editor.editor_shortcuts.no_qa_issue_is_loaded",
                                "No QA issue is loaded. Scroll the queue for more.",
                            ),
                        )
                    }
                }

                EditorShortcutAction.CopySource -> {
                    event.preventDefault()
                    onCopySource()
                }

                EditorShortcutAction.InsertNextPlaceholder -> {
                    val placeholder = state.nextPlaceholder() ?: return@EventListener
                    event.preventDefault()
                    onInsertText(placeholder)
                }

                // The locale's marks, resolved at press time from the locale being edited rather
                // than captured when the handler was built — the editor's locale comes from the URL
                // and a route change must not leave the chords typing the previous language's pair.
                EditorShortcutAction.InsertOpeningQuote -> {
                    event.preventDefault()
                    onInsertText(dev.lingvo.shared.quoteStyleFor(state.locale).open)
                }

                EditorShortcutAction.InsertClosingQuote -> {
                    event.preventDefault()
                    onInsertText(dev.lingvo.shared.quoteStyleFor(state.locale).close)
                }

                is EditorShortcutAction.ActivateAssistResult -> {
                    val row = state.selectedRow ?: return@EventListener
                    val resolution = resolveAssistShortcutResult(
                        workspace = workspace,
                        ranked = rankedAssistResults(
                            proposal = state.proposalsByKey[row.key.id],
                            suggestions = state.panels.rankedSuggestions(row),
                        ),
                        index = action.index,
                    )
                    when (resolution) {
                        EditorAssistShortcutResolution.Unavailable -> return@EventListener
                        is EditorAssistShortcutResolution.Review -> {
                            val reviewAction = currentAssistAction(
                                id = resolution.actionId,
                                expectedIdentity = resolution.actionIdentity,
                            )
                                ?: return@EventListener
                            event.preventDefault()
                            reviewAction.focus()
                        }

                        is EditorAssistShortcutResolution.Apply -> {
                            val result = resolution.result
                            if (
                                currentAssistAction(
                                    id = result.visibleActionId(),
                                    expectedIdentity = result.actionIdentity(),
                                ) == null
                            ) {
                                return@EventListener
                            }
                            event.preventDefault()
                            when (result) {
                                is EditorAssistResult.Proposal ->
                                    state.decideProposal(row.key.id, accept = true)

                                is EditorAssistResult.Suggestion -> onInsertSuggestion(result.suggestion)
                            }
                        }
                    }
                }

                EditorShortcutAction.CloseTopSurface -> {
                    event.preventDefault()
                    onCloseTopSurface()
                }

                // Selection and focus move together. These used to call `state.moveSelection`
                // directly, which changed `selectedKeyId` and `aria-current` but left DOM focus on
                // the row the person started from — so two rows wore an identical accent ring at
                // once, the editor pane showed a third thing, and a screen reader announced
                // nothing at all. `onMoveSelection` moves both.
                EditorShortcutAction.PreviousKey -> {
                    event.preventDefault()
                    onMoveSelection(-1)
                }

                EditorShortcutAction.NextKey -> {
                    event.preventDefault()
                    onMoveSelection(1)
                }

                EditorShortcutAction.FocusTarget -> {
                    event.preventDefault()
                    onFocusTarget()
                }

                EditorShortcutAction.FocusSearch -> {
                    event.preventDefault()
                    onFocusSearch()
                }

                EditorShortcutAction.OpenHistory -> {
                    event.preventDefault()
                    onOpenHistory()
                }

                EditorShortcutAction.MachineTranslate -> {
                    val row = state.selectedRow ?: return@EventListener
                    val provider = defaultMachineProvider(state.projectSlug, state.enabledProviders)
                        ?: return@EventListener
                    event.preventDefault()
                    state.panels.translate(row, provider)
                }

                EditorShortcutAction.OpenShortcutHelp -> {
                    event.preventDefault()
                    onOpenShortcutHelp()
                }
            }
        }
        addEditorKeydownListener(listener)
        onDispose { removeEditorKeydownListener(listener) }
    }
}

private fun currentAssistAction(
    id: String,
    expectedIdentity: String,
): HTMLElement? {
    val action = document.getElementById(id) as? HTMLElement ?: return null
    if (!action.isConnected) return null
    if (action.closest("[inert], [aria-hidden='true']") != null) return null
    if (action.getAttribute(EDITOR_ASSIST_RESULT_IDENTITY_ATTRIBUTE) != expectedIdentity) return null
    return action
}

private fun KeyboardEvent.isUnmodifiedNativeActivation(target: Element?): Boolean =
    key in setOf("Enter", " ") &&
        !altKey &&
        !ctrlKey &&
        !metaKey &&
        target?.closest(NATIVE_ACTIVATION_SELECTOR) != null

private const val NATIVE_ACTIVATION_SELECTOR =
    "button, a[href], input[type='button'], input[type='submit'], [role='button'], [role='tab']"

/** Shared by review and editor keyboard guards. */
internal fun Element?.isTextEntry(): Boolean {
    val element = this as? HTMLElement ?: return false
    return element.tagName in setOf("INPUT", "TEXTAREA", "SELECT") ||
        element.getAttribute("contenteditable") == "true"
}

/** No target at all, or an empty one — the two shapes "untranslated" takes in a queue row. */
private fun TranslationState?.isUntranslated(): Boolean =
    this == null || this == TranslationState.EMPTY
